import { useParams, usePathname } from 'next/navigation';

// Utils
import { formatUrlPathname, formatUrls } from '@/utils/urls/formatUrl';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Lib
import { groupHasAccess, networkHasAccess } from '@/lib/acl/pageHasAccess';

// Components
import { GroupEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/GroupEmptyState';
import { SocialProfileEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/SocialProfileEmptyState';
import { MultiProfilesEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/MultiProfilesEmptyState';

type PageContentWrapperProps = {
    children: React.ReactNode;
};

function formatUrlKeys<TData extends typeof networkHasAccess>(
    obj: TData,
    pathname: ReturnType<typeof useParams>
) {
    const modifiedObject = {} as TData;

    Object.keys(obj).forEach((url) => {
        const modifiedKey = formatUrlPathname(url as string, pathname);
        modifiedObject[modifiedKey] = obj[url];
    });

    return modifiedObject;
}

export function PageContentWrapper({ children }: PageContentWrapperProps) {
    const pathname = usePathname();
    const params = useParams();

    const networkHasAccessFormatted = formatUrlKeys(networkHasAccess, params);

    const { activeMultiSelectionProfiles } = useProfilesStore((state) => state);

    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();

    if (
        activeSocialProfile &&
        pathname in networkHasAccessFormatted &&
        !networkHasAccessFormatted[pathname].includes(activeSocialProfile.social_network_type)
    ) {
        return <SocialProfileEmptyState />;
    }

    if (
        activeGroup &&
        !activeSocialProfile &&
        !formatUrls(groupHasAccess, params).includes(pathname)
    ) {
        return <GroupEmptyState />;
    }

    if (activeMultiSelectionProfiles?.socialProfileIds.length && !activeSocialProfile) {
        return <MultiProfilesEmptyState />;
    }

    return children;
}
